/**
 * useEmailTemplates Hook
 *
 * Manages email templates for the Email Template Generator.
 * Stores templates in PostgreSQL via the userSettingsService.
 */

import { useState, useCallback, useEffect } from 'react';
import { getSetting, setSetting } from '@shared/services/userSettingsService';
import type {
  SectionTemplate,
  FullEmailTemplate,
} from '../types/emailTemplates';

// Namespace for HMC email templates in PostgreSQL
const TEMPLATES_NAMESPACE = 'hmc-email-templates';

// Setting keys
const SETTING_KEYS = {
  personalMessageTemplates: 'personal-message-templates',
  signatureTemplates: 'signature-templates',
  subjectTemplates: 'subject-templates',
  fullTemplates: 'full-templates',
} as const;

/**
 * Generate a unique ID for templates
 */
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

/**
 * Hook for managing email templates
 */
export function useEmailTemplates() {
  // Section templates
  const [personalMessageTemplates, setPersonalMessageTemplates] = useState<SectionTemplate[]>([]);
  const [signatureTemplates, setSignatureTemplates] = useState<SectionTemplate[]>([]);
  const [subjectTemplates, setSubjectTemplates] = useState<SectionTemplate[]>([]);

  // Full templates
  const [fullTemplates, setFullTemplates] = useState<FullEmailTemplate[]>([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load templates from backend on mount
  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [personalMsg, signatures, subjects, full] = await Promise.all([
          getSetting<SectionTemplate[]>(SETTING_KEYS.personalMessageTemplates, {
            namespace: TEMPLATES_NAMESPACE,
            defaultValue: [],
          }),
          getSetting<SectionTemplate[]>(SETTING_KEYS.signatureTemplates, {
            namespace: TEMPLATES_NAMESPACE,
            defaultValue: [],
          }),
          getSetting<SectionTemplate[]>(SETTING_KEYS.subjectTemplates, {
            namespace: TEMPLATES_NAMESPACE,
            defaultValue: [],
          }),
          getSetting<FullEmailTemplate[]>(SETTING_KEYS.fullTemplates, {
            namespace: TEMPLATES_NAMESPACE,
            defaultValue: [],
          }),
        ]);

        setPersonalMessageTemplates(personalMsg || []);
        setSignatureTemplates(signatures || []);
        setSubjectTemplates(subjects || []);
        setFullTemplates(full || []);
      } catch (err) {
        console.error('[useEmailTemplates] Failed to load templates:', err);
        setError('Failed to load templates');
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Save personal message template
  const savePersonalMessageTemplate = useCallback(async (name: string, content: string) => {
    const now = new Date().toISOString();
    const newTemplate: SectionTemplate = {
      id: generateId(),
      name,
      content,
      createdAt: now,
      updatedAt: now,
    };

    const updated = [...personalMessageTemplates, newTemplate];
    setPersonalMessageTemplates(updated);

    try {
      await setSetting(SETTING_KEYS.personalMessageTemplates, updated, {
        namespace: TEMPLATES_NAMESPACE,
      });
      return newTemplate;
    } catch (err) {
      console.error('[useEmailTemplates] Failed to save personal message template:', err);
      // Rollback
      setPersonalMessageTemplates(personalMessageTemplates);
      throw err;
    }
  }, [personalMessageTemplates]);

  // Save signature template
  const saveSignatureTemplate = useCallback(async (name: string, content: string) => {
    const now = new Date().toISOString();
    const newTemplate: SectionTemplate = {
      id: generateId(),
      name,
      content,
      createdAt: now,
      updatedAt: now,
    };

    const updated = [...signatureTemplates, newTemplate];
    setSignatureTemplates(updated);

    try {
      await setSetting(SETTING_KEYS.signatureTemplates, updated, {
        namespace: TEMPLATES_NAMESPACE,
      });
      return newTemplate;
    } catch (err) {
      console.error('[useEmailTemplates] Failed to save signature template:', err);
      setSignatureTemplates(signatureTemplates);
      throw err;
    }
  }, [signatureTemplates]);

  // Save subject template
  const saveSubjectTemplate = useCallback(async (name: string, content: string) => {
    const now = new Date().toISOString();
    const newTemplate: SectionTemplate = {
      id: generateId(),
      name,
      content,
      createdAt: now,
      updatedAt: now,
    };

    const updated = [...subjectTemplates, newTemplate];
    setSubjectTemplates(updated);

    try {
      await setSetting(SETTING_KEYS.subjectTemplates, updated, {
        namespace: TEMPLATES_NAMESPACE,
      });
      return newTemplate;
    } catch (err) {
      console.error('[useEmailTemplates] Failed to save subject template:', err);
      setSubjectTemplates(subjectTemplates);
      throw err;
    }
  }, [subjectTemplates]);

  // Save full template
  const saveFullTemplate = useCallback(async (template: Omit<FullEmailTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
    const now = new Date().toISOString();
    const newTemplate: FullEmailTemplate = {
      ...template,
      id: generateId(),
      createdAt: now,
      updatedAt: now,
    };

    const updated = [...fullTemplates, newTemplate];
    setFullTemplates(updated);

    try {
      await setSetting(SETTING_KEYS.fullTemplates, updated, {
        namespace: TEMPLATES_NAMESPACE,
      });
      return newTemplate;
    } catch (err) {
      console.error('[useEmailTemplates] Failed to save full template:', err);
      setFullTemplates(fullTemplates);
      throw err;
    }
  }, [fullTemplates]);

  // Delete personal message template
  const deletePersonalMessageTemplate = useCallback(async (id: string) => {
    const updated = personalMessageTemplates.filter((t) => t.id !== id);
    setPersonalMessageTemplates(updated);

    try {
      await setSetting(SETTING_KEYS.personalMessageTemplates, updated, {
        namespace: TEMPLATES_NAMESPACE,
      });
    } catch (err) {
      console.error('[useEmailTemplates] Failed to delete personal message template:', err);
      setPersonalMessageTemplates(personalMessageTemplates);
      throw err;
    }
  }, [personalMessageTemplates]);

  // Delete signature template
  const deleteSignatureTemplate = useCallback(async (id: string) => {
    const updated = signatureTemplates.filter((t) => t.id !== id);
    setSignatureTemplates(updated);

    try {
      await setSetting(SETTING_KEYS.signatureTemplates, updated, {
        namespace: TEMPLATES_NAMESPACE,
      });
    } catch (err) {
      console.error('[useEmailTemplates] Failed to delete signature template:', err);
      setSignatureTemplates(signatureTemplates);
      throw err;
    }
  }, [signatureTemplates]);

  // Delete subject template
  const deleteSubjectTemplate = useCallback(async (id: string) => {
    const updated = subjectTemplates.filter((t) => t.id !== id);
    setSubjectTemplates(updated);

    try {
      await setSetting(SETTING_KEYS.subjectTemplates, updated, {
        namespace: TEMPLATES_NAMESPACE,
      });
    } catch (err) {
      console.error('[useEmailTemplates] Failed to delete subject template:', err);
      setSubjectTemplates(subjectTemplates);
      throw err;
    }
  }, [subjectTemplates]);

  // Delete full template
  const deleteFullTemplate = useCallback(async (id: string) => {
    const updated = fullTemplates.filter((t) => t.id !== id);
    setFullTemplates(updated);

    try {
      await setSetting(SETTING_KEYS.fullTemplates, updated, {
        namespace: TEMPLATES_NAMESPACE,
      });
    } catch (err) {
      console.error('[useEmailTemplates] Failed to delete full template:', err);
      setFullTemplates(fullTemplates);
      throw err;
    }
  }, [fullTemplates]);

  return {
    // State
    personalMessageTemplates,
    signatureTemplates,
    subjectTemplates,
    fullTemplates,
    isLoading,
    error,

    // Actions
    savePersonalMessageTemplate,
    saveSignatureTemplate,
    saveSubjectTemplate,
    saveFullTemplate,
    deletePersonalMessageTemplate,
    deleteSignatureTemplate,
    deleteSubjectTemplate,
    deleteFullTemplate,
  };
}

export default useEmailTemplates;
